import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {mdiSend} from '@mdi/js';
import MdiIcon from '@mdi/react';
import {Form, Formik, FormikHelpers} from 'formik';

import EmojiPicker from 'components/EmojiPicker';
import Loader from 'components/Loader';
import MentionTextarea from 'components/MentionTextarea';
import OutlineButton from 'components/OutlineButton';
import {createComment} from 'dispatchers/comments';
import {useToggle} from 'hooks';
import CurrencySelectModal from 'modals/CurrencySelectModal';
import {getComments, getManager} from 'selectors/state';
import {AppDispatch, Comment as TComment, SFC, UserReadSerializer} from 'types';
import {displayErrorToast} from 'utils/toasts';
import yup from 'utils/yup';

import Comment from './Comment';
import * as S from './Styles';

export interface CommentsProps {
  postId: number;
}

const Comments: SFC<CommentsProps> = ({className, postId}) => {
  const [commentDetails, setCommentDetails] = useState<TComment[]>([]);
  const [currencySelectModalIsOpen, toggleCurrencySelectModal] = useToggle(false);
  const [mentionedUsers, setMentionedUsers] = useState<UserReadSerializer[]>([]);
  const [startIndex, setStartIndex] = useState<number>(0);
  const comments = useSelector(getComments);
  const dispatch = useDispatch<AppDispatch>();
  const manager = useSelector(getManager);

  const initialValues = {
    content: '',
    price_amount: '',
  };

  type FormValues = typeof initialValues;

  const commentList = useMemo(() => {
    const _comments = Object.values(comments);
    return _comments.filter(({post}) => post === postId);
  }, [comments, postId]);

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      content: yup.string().required('Content is required'),
    });
  }, []);

  const handleComment = useCallback(() => {
    const nextIndex = startIndex + 4;
    if (nextIndex < commentList.length) {
      setStartIndex(nextIndex);
      setCommentDetails((prevDetails) => {
        const nextComments = commentList.slice(startIndex, nextIndex);
        return [...prevDetails, ...nextComments];
      });
    }
  }, [commentList, startIndex]);

  useEffect(() => {
    const arr = commentList.slice(0, startIndex + 4);
    setCommentDetails(arr);
  }, [commentList, startIndex]);

  const handleSubmit = async (values: FormValues, {resetForm}: FormikHelpers<FormValues>): Promise<void> => {
    try {
      let price_amount = values.price_amount === '' ? null : parseInt(values.price_amount, 10);
      let price_currency = manager.activeCommentCurrency?.id || null;

      if (price_amount && !price_currency) {
        displayErrorToast('Please select a currency for the amount');
        return;
      }

      if (!price_amount || !price_currency) {
        price_amount = null;
        price_currency = null;
      }

      const requestData = {
        ...values,
        mentioned_user_ids: mentionedUsers.map((u) => u.id),
        post: postId,
        price_amount,
        price_currency,
      };
      await dispatch(createComment(requestData));
      resetForm();
      setMentionedUsers([]);
    } catch (error) {
      const errorData = (error as {response?: {data?: unknown}}).response?.data;
      const errorText = JSON.stringify(errorData || '').toLowerCase();

      if (errorText.includes('insufficient funds')) {
        const tipCurrencyTicker = manager.activeCommentCurrency?.ticker;
        displayErrorToast(
          tipCurrencyTicker
            ? `You don't have enough ${tipCurrencyTicker} to send this tip amount`
            : "You don't have enough funds to send this tip amount",
        );
        return;
      }

      displayErrorToast('Error submitting the comment');
    }
  };

  const handleTipCurrencyButtonClick = () => {
    toggleCurrencySelectModal();
  };

  const renderComments = () => {
    return commentDetails.map((comment, index) => <Comment comment={comment} isFirst={index === 0} key={index} />);
  };

  const renderTipCurrencyControl = (errors: {[field: string]: string}, touched: {[field: string]: boolean}) => {
    if (!manager.activeCommentCurrency) {
      return (
        <S.TipCurrencyButton onClick={handleTipCurrencyButtonClick} type="button">
          Tip Currency
        </S.TipCurrencyButton>
      );
    }

    return (
      <S.PriceAmountInputContainer>
        <S.IconContainer aria-label="Change tip currency" onClick={handleTipCurrencyButtonClick} type="button">
          <S.Image alt={`${manager.activeCommentCurrency.ticker} logo`} src={manager.activeCommentCurrency.logo} />
        </S.IconContainer>
        <S.PriceAmountInput errors={errors} name="price_amount" placeholder="Amount" touched={touched} type="number" />
      </S.PriceAmountInputContainer>
    );
  };

  return (
    <>
      <S.Container className={className}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnMount={false}
          validationSchema={validationSchema}
        >
          {({dirty, errors, isSubmitting, isValid, touched, values, setFieldValue}) => (
            <Form>
              <S.CommentForm>
                <S.MentionTextareaWrapper>
                  <MentionTextarea
                    dropdownYOffset={36}
                    errors={errors}
                    label=""
                    name="content"
                    onChange={(e) => setFieldValue('content', e.target.value)}
                    onMentionedUsersChange={setMentionedUsers}
                    placeholder="Add a comment..."
                    touched={touched}
                    value={values.content}
                  />
                </S.MentionTextareaWrapper>
                <S.ControlsWrapper>
                  <EmojiPicker field="content" setFieldValue={setFieldValue} value={values.content} />
                  {renderTipCurrencyControl(errors, touched)}
                  <S.SendButton aria-label="Post comment" disabled={!dirty || isSubmitting || !isValid} type="submit">
                    {isSubmitting ? <Loader size={12} /> : <MdiIcon path={mdiSend} size="18px" />}
                  </S.SendButton>
                </S.ControlsWrapper>
              </S.CommentForm>
            </Form>
          )}
        </Formik>
        {renderComments()}
        {commentList.length > commentDetails.length && (
          <S.Content>
            <S.Divider />
            <OutlineButton
              onClick={handleComment}
              text={`Show ${Math.min(4, commentList.length - commentDetails.length)} more comments`}
            />
            <S.Divider />
          </S.Content>
        )}
      </S.Container>
      {currencySelectModalIsOpen ? <CurrencySelectModal close={() => toggleCurrencySelectModal(false)} /> : null}
    </>
  );
};

export default Comments;
